import { ShowBase, useRecordContext, useRedirect, useTranslate } from "ra-core";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { DeleteButton } from "@/components/admin/delete-button";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceField } from "@/components/admin/reference-field";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import { CallsSection } from "../calls";
import { MailSection } from "../mail";
import { CanDelete } from "../misc/CanDelete";
import { FromBitrixBadge, NotFromBitrix } from "../misc/fromBitrix";
import { RelativeDate } from "../misc/RelativeDate";
import { useProjects } from "../projects/useCurrentProject";
import type { Lead } from "../types";
import { ConvertButton } from "./ConvertButton";
import { normalizePhone } from "./phone";
import { leadRepresentation } from "./sources";

export const LeadShow = ({ open, id }: { open: boolean; id?: string }) => {
  const redirect = useRedirect();
  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) =>
        !isOpen &&
        redirect("/leads", undefined, undefined, undefined, {
          _scrollToTop: false,
        })
      }
    >
      <DialogContent className="lg:max-w-2xl p-4 md:p-6 overflow-y-auto max-h-9/10 top-1/20 translate-y-0">
        {id ? (
          <ShowBase id={id} resource="leads">
            <LeadShowContent />
          </ShowBase>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const LeadShowContent = () => {
  const translate = useTranslate();
  const lead = useRecordContext<Lead>();
  const { projects, stages } = useProjects();
  if (!lead) return null;
  const stage = stages.find((s) => String(s.id) === String(lead.stage_id));
  const project = projects.find(
    (p) => String(p.id) === String(lead.project_id),
  );
  const tel = normalizePhone(lead.phone);

  return (
    <div className="flex flex-col gap-6 min-w-0">
      <div className="flex flex-col gap-2 pr-8">
        <DialogTitle className="text-xl font-semibold break-words">
          {leadRepresentation(lead)}
        </DialogTitle>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="secondary">
            {translate(`crm.leads.sources.${lead.source}`)}
          </Badge>
          {stage ? <Badge variant="outline">{stage.name}</Badge> : null}
          <FromBitrixBadge record={lead} />
          {project && projects.length > 1 ? <span>{project.name}</span> : null}
          <span>
            <RelativeDate date={lead.created_at} />
          </span>
        </div>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Field label={translate("resources.leads.fields.phone")}>
          {lead.phone ? (
            <a
              href={tel ? `tel:+${tel}` : `tel:${lead.phone}`}
              className="underline"
            >
              {lead.phone}
            </a>
          ) : null}
        </Field>
        <Field label={translate("resources.leads.fields.email")}>
          {lead.email ? (
            <a href={`mailto:${lead.email}`} className="underline break-all">
              {lead.email}
            </a>
          ) : null}
        </Field>
        <Field label={translate("resources.leads.fields.company_name")}>
          {lead.company_name}
        </Field>
        <Field label={translate("resources.leads.fields.sales_id")}>
          {lead.sales_id != null ? (
            <ReferenceField source="sales_id" reference="sales" link={false} />
          ) : (
            translate("crm.leads.no_responsible")
          )}
        </Field>
        {lead.comment ? (
          <Field
            label={translate("resources.leads.fields.comment")}
            className="sm:col-span-2"
          >
            <span className="whitespace-pre-line">{lead.comment}</span>
          </Field>
        ) : null}
        {lead.contact_id != null ? (
          <Field label={translate("resources.leads.fields.contact_id")}>
            <Link
              to={`/contacts/${lead.contact_id}/show`}
              className="underline"
            >
              <ReferenceField
                source="contact_id"
                reference="contacts"
                link={false}
              />
            </Link>
          </Field>
        ) : null}
      </dl>

      <CallsSection filter={{ lead_id: lead.id }} phone={lead.phone} />

      <MailSection card={{ leadId: lead.id }} defaultTo={lead.email} />

      {/* a Bitrix24 lead in Bitrix24 mode is worked on in Bitrix24 */}
      <NotFromBitrix record={lead}>
        <div className="flex flex-wrap gap-2">
          <ConvertButton lead={lead} />
          <EditButton />
          <CanDelete resource="leads">
            <DeleteButton />
          </CanDelete>
        </div>
      </NotFromBitrix>
    </div>
  );
};

const Field = ({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) =>
  children ? (
    <div className={`min-w-0 ${className ?? ""}`}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words">{children}</dd>
    </div>
  ) : null;
